import * as R from 'ramda';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { localize } from 'common/utils';

import { VenueWithSharedMenuLinkInfo } from './useVenuesWithSelectionLinks';
import { SharedMenu } from '../types';
import { getSelectionState, getTypedKeys, selectionHasValidLastPublishedValue } from '../utils';

type UseSelectionListArgs = {
  selections: SharedMenu[];
  venuesWithSelectionLinks?: VenueWithSharedMenuLinkInfo[];
};

export type SortBy = 'name' | 'status' | 'venue-count' | 'last-published';
export type SortingOrder = 'ASC' | 'DESC';
export type SortByState = Partial<Record<SortBy, SortingOrder>> | undefined;

export const useSelectionList = ({ selections, venuesWithSelectionLinks = [] }: UseSelectionListArgs) => {
  const intl = useIntl();
  const [searchInput, setSearchInput] = useState('');
  const [appliedSortBy, setAppliedSortBy] = useState<SortByState>();

  const handleSearch = useCallback((input: string) => {
    setSearchInput(input);
  }, []);

  const handleSorting = useCallback(
    (sortingProperty: SortBy) => {
      const updatedSortBy: SortByState =
        appliedSortBy?.[sortingProperty] === 'ASC' ? { [sortingProperty]: 'DESC' } : { [sortingProperty]: 'ASC' };
      setAppliedSortBy(updatedSortBy);
    },
    [appliedSortBy],
  );

  const searchSelections = useCallback(() => {
    const trimmedInput = searchInput.trim();
    if (!trimmedInput) {
      return selections;
    }

    const matchingSelectionsByName = selections.filter(selection =>
      localize(selection.name, intl.locale).toLocaleLowerCase().includes(trimmedInput.toLocaleLowerCase()),
    );

    const matchingVenuesByName = venuesWithSelectionLinks.filter(venue =>
      localize(venue.name, intl.locale).toLocaleLowerCase().includes(trimmedInput.toLocaleLowerCase()),
    );

    const selectionIdsByMatchingVenues = matchingVenuesByName.reduce<string[]>(
      (acc, curr) =>
        curr.sharedMenuLink.scope !== 'none' && !!curr.sharedMenuLink.sharedMenuId
          ? [...acc, curr.sharedMenuLink.sharedMenuId]
          : acc,
      [],
    );

    const selectionsUsedByMatchingVenues = selections.filter(selection =>
      selectionIdsByMatchingVenues.includes(selection.id),
    );

    const uniqueSelections = R.uniqBy(
      selection => selection.id,
      [...matchingSelectionsByName, ...selectionsUsedByMatchingVenues],
    );

    return uniqueSelections;
  }, [intl.locale, searchInput, selections, venuesWithSelectionLinks]);

  const sortSelections = useCallback(
    (selectionsToSort: SharedMenu[]) => {
      if (!appliedSortBy || !selectionsToSort || !selectionsToSort.length) {
        return selectionsToSort;
      }

      const attributeResolver: Record<SortBy, (selection: SharedMenu) => string | number> = {
        name: selection => localize(selection.name, intl.locale),
        status: selection => getSelectionState(selection),
        'last-published': selection =>
          selectionHasValidLastPublishedValue(selection) ? selection.version.updated_at : '-',
        'venue-count': selection => selection.linked_venue_ids.length,
      };

      const [currAppliedSortBy] = getTypedKeys(appliedSortBy);

      return R.sort((selectionA, selectionB) => {
        const valueToCompareA = attributeResolver[currAppliedSortBy](selectionA);
        const valueToCompareB = attributeResolver[currAppliedSortBy](selectionB);

        if (typeof valueToCompareA === 'string' && typeof valueToCompareB === 'string') {
          return appliedSortBy?.[currAppliedSortBy] === 'ASC'
            ? valueToCompareA.localeCompare(valueToCompareB)
            : valueToCompareB.localeCompare(valueToCompareA);
        }

        if (typeof valueToCompareA === 'number' && typeof valueToCompareB === 'number') {
          return appliedSortBy?.[currAppliedSortBy] === 'ASC'
            ? valueToCompareA - valueToCompareB
            : valueToCompareB - valueToCompareA;
        }

        return 0;
      }, selectionsToSort);
    },
    [appliedSortBy, intl],
  );

  const shownSelections = useMemo(() => {
    return sortSelections(searchSelections());
  }, [searchSelections, sortSelections]);

  return {
    shownSelections,
    searchSelections: handleSearch,
    searchInput,
    handleSorting,
    appliedSortBy,
  };
};
